import { OperationVariables, useQuery } from '@apollo/client';
import { forwardRef, Fragment } from 'react';
import { Typography, Divider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import LoadingScreen from '@lwtears/lwt-common-frontend/lib/LoadingScreen';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import GET_PRODUCT_CURRICULUM from 'graph/queries/product-curriculums/get-product-curriculum';
import GET_STUDENT_CHECKLIST_OUTCOMES from 'graph/queries/checklists/get-student-checklist-outcomes';
import {
  Assessment,
  AssessmentReport,
  AssessmentType,
  BenchmarkAssessment,
  Domain,
  ProductCurriculum,
  Student,
  StudentChecklistOutcome,
  UnitAssessment,
  WeeklyChecklist,
} from 'types';
import AssessmentScore from 'components/common/AssessmentScore';
import { useFindOneStudentAssessmentStore } from 'graph/state/query-hooks/use-student-assessment';
import { NotepadSrc } from 'components/common/NotepadSrc';
import { getCurriculumBenchmarkAssessment } from 'util/assessments-util';

const t = (key) => translate('ui', key);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((theme: Theme) => {
  return {
    dFlex: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    pt4: {
      paddingTop: theme.spacing(4),
    },
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    pageHeader: {
      display: 'flex',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
      justifyContent: 'space-between',
    },
    separator: {
      backgroundColor: theme.palette.secondary.main,
      height: '2px',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    secondaryLink: {
      color: theme.palette.black.main,
      fontFamily: 'HWT Futura',
    },
    section: {
      backgroundColor: 'white',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(8),
      padding: theme.spacing(4),
      border: `1px solid ${(theme.palette as any).grey[600]}`,
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',

      '&>h2': {
        color: theme.palette.primary.main,
      },
    },
    ul: {
      listStyle: 'none',
      padding: 0,
    },
    twoColumnGrid: {
      display: 'grid',
      gridTemplateColumns: '50% 50%',
      marginTop: theme.spacing(4),

      '&>li': {
        display: 'grid',
        gridTemplateColumns: '1fr .25fr',
        gridColumnGap: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        fontFamily: 'HWT Futura',

        '&:not(:last-of-type)': {
          borderBottom: `1px solid ${(theme.palette as any).grey[600]}`,
        },
      },
    },
    vertTab: {
      height: '100%',
      margin: '0',
      borderLeft: `3px solid ${(theme.palette as any).grey[400]}`,

      '&>li': {
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginLeft: '-3px',
        borderLeft: '3px solid transparent',

        '&>a': {
          color: theme.palette.black.main,
        },
      },
    },
  };
});

interface AssessmentProps {
  assessment: Partial<Assessment>;
  curriculumId: number;
  student: Partial<Student>;
  weeklyChecklists?: Partial<WeeklyChecklist>[];
  domains?: Partial<Domain>[];
  assessmentReport?: Partial<AssessmentReport>;
  checklistOutcomes?: Partial<StudentChecklistOutcome>[];
}

const PrintAssessmentDetails = forwardRef<HTMLDivElement, AssessmentProps>((props, ref) => {
  const productCurriculum: ProductCurriculum | undefined = useQuery(GET_PRODUCT_CURRICULUM, {
    variables: { where: { id: props.curriculumId } },
  } as OperationVariables).data?.productCurriculum;

  const [studentAssessment] = useFindOneStudentAssessmentStore(
    props.assessment.id!,
    props.student.id!
  );

  const weeklyOutcomes: Partial<StudentChecklistOutcome>[] | undefined = useQuery(
    GET_STUDENT_CHECKLIST_OUTCOMES,
    {
      variables: {
        where: {
          checklistId: props.weeklyChecklists?.map((checklist) => {
            return checklist.id;
          }),
          studentId: props.student.id,
        },
      },
      skip: props.weeklyChecklists === undefined,
    }
  ).data?.studentChecklistOutcomes;

  if (productCurriculum === undefined) {
    return <LoadingScreen backgroundImage={NotepadSrc} />;
  }

  return (
    <div ref={ref}>
      {/* TODO: Change to get curriculum name and grade from assessment when they update API */}
      <Typography variant="h1">{productCurriculum.name}</Typography>
      <Typography>{productCurriculum.gradeLevel}</Typography>
      <Typography>{`Student: ${props.student.firstName} ${props.student.lastName}`}</Typography>
      <Typography>{`Test: ${
        props.assessment.type === AssessmentType.Benchmark
          ? t(
              'assessments.benchmarkPeriod-' +
                getCurriculumBenchmarkAssessment(
                  props.assessment as BenchmarkAssessment,
                  props.curriculumId
                ).period
            )
          : (props.assessment as UnitAssessment).unit.title
      }`}</Typography>
      {props.assessmentReport && (
        <>
          {studentAssessment.studentAssessment?.comments && (
            <>
              <Typography variant="h2">{t('assessments.comments')}</Typography>
              <Typography>{studentAssessment.studentAssessment.comments}</Typography>
            </>
          )}
          <Typography variant="h2">{`${t('assessments.overall')}:`}</Typography>
          <AssessmentScore
            score={
              props.assessmentReport.totalStats?.find((v) =>
                v.studentIds.includes(props.student.id!)
              )?.score
            }
          />
        </>
      )}
      {props.domains?.map((domain) => {
        const domainAssessmentScores = props.assessmentReport?.reportDomains?.find(
          (v) => v.domainId === domain.id
        );
        const sectionAssessmentScores = props.assessmentReport?.reportSections?.filter(
          (reportSection) =>
            props.assessment.assessmentDomains
              ?.find((assessmentDomain) => assessmentDomain.domain.id === domain.id)
              ?.assessmentSections.some(
                (domainSection) => domainSection.id === reportSection.assessmentSectionId
              )
        );
        const checklistScores = props.checklistOutcomes?.filter(
          (v) => v.observation?.domainId === domain.id
        );
        return (
          <div key={`domain-${domain.id}`}>
            {(domainAssessmentScores || sectionAssessmentScores || checklistScores) && (
              <>
                <Divider />
                <Typography variant="h2">{domain.title}</Typography>
              </>
            )}
            {domainAssessmentScores?.stats && domainAssessmentScores.stats.length > 0 && (
              <AssessmentScore score={domainAssessmentScores.stats[0].score} />
            )}
            {sectionAssessmentScores && sectionAssessmentScores.length > 0 && (
              <>
                <Typography variant="h3">{t('assessments.demonstrations')}</Typography>
                {sectionAssessmentScores.map((section) => {
                  return (
                    <Fragment key={`assessment-section-${section.assessmentSectionId}`}>
                      <Typography>{section.assessmentSection.title}</Typography>
                      {section.stats && section.stats.length > 0 && (
                        <AssessmentScore score={section.stats[0].score} />
                      )}
                    </Fragment>
                  );
                })}
              </>
            )}
            {checklistScores && checklistScores.length > 0 && (
              <>
                <Typography variant="h3">{t('assessments.observations')}</Typography>
                {checklistScores.map((outcome) => {
                  return (
                    <Fragment key={`checklist-outcome-${outcome.id}`}>
                      <Typography>{outcome.observation?.title}</Typography>
                      <AssessmentScore score={outcome.score} />
                    </Fragment>
                  );
                })}
              </>
            )}
            {props.weeklyChecklists &&
              weeklyOutcomes &&
              weeklyOutcomes.filter((outcome) => outcome.observation?.domainId === domain.id)
                .length > 0 && (
                <>
                  <Typography variant="h3">{t('assessments.weeklyChecklists')}</Typography>
                  {props.weeklyChecklists.map((checklist) => {
                    const filteredOutcomes = weeklyOutcomes.filter(
                      (outcome) =>
                        outcome.checklistId === checklist.id &&
                        outcome.observation?.domainId === domain.id
                    );
                    if (filteredOutcomes.length > 0) {
                      return (
                        <Fragment key={`checklist-${checklist.id}`}>
                          <Typography variant="h4">{`${t('assessments.week')} ${
                            checklist.weekNumber
                          }`}</Typography>
                          {filteredOutcomes.map((outcome) => {
                            return (
                              <Fragment
                                key={`checklist-outcome-${checklist.id}-${outcome.observationId}`}
                              >
                                <Typography>{outcome.observation?.title}</Typography>
                                <AssessmentScore score={outcome.score} />
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      );
                    } else {
                      return null;
                    }
                  })}
                </>
              )}
          </div>
        );
      })}
    </div>
  );
});

export default PrintAssessmentDetails;
